// /home — 5-tile non-scroll home grid.
// Uses chassis BigButton primitives + i18n.

import type { RouteRenderer, HomeTileSpec } from "../types.ts";
import { makeBilingual, getSelectedLang, screenReaderAnnounce } from "../chassis/elder/index.ts";
import { i18n } from "../chassis/elder/i18n-strings.ts";
import { listAccepted, subscribeToPinChanges } from "../chassis/elder/remote-pin.ts";
import { exitOverlay, navigate } from "../router.ts";

const BASE_TILES: readonly HomeTileSpec[] = [
  {
    id: "phone",
    i18nKey: "home.tile.phone",
    emoji: "📞",
    variant: "primary",
    action: () => {
      window.location.href = "tel:";
    },
  },
  {
    id: "whatsapp",
    i18nKey: "home.tile.whatsapp",
    emoji: "💬",
    variant: "default",
    action: () => {
      window.location.href = "https://wa.me/";
    },
  },
  {
    id: "camera",
    i18nKey: "home.tile.camera",
    emoji: "📷",
    variant: "default",
    action: () => {
      window.location.href = "intent://#Intent;type=image/*;action=android.media.action.IMAGE_CAPTURE;end";
    },
  },
  {
    id: "photos",
    i18nKey: "home.tile.photos",
    emoji: "🖼️",
    variant: "default",
    action: () => {
      window.location.href = "content://media/internal/images/media";
    },
  },
  {
    id: "sos",
    i18nKey: "home.tile.sos",
    emoji: "🆘",
    variant: "danger",
    action: () => {
      navigate("sos");
    },
  },
] as const;

export const renderHome: RouteRenderer = (host, _params) => {
  const lang = getSelectedLang();
  const shell = document.createElement("div");
  shell.className = "sarathi-route-shell";

  const header = document.createElement("header");
  const back = document.createElement("button");
  back.type = "button";
  back.setAttribute("aria-label", makeBilingual(i18n, "nav.back", lang).primary);
  back.style.cssText = "min-width:88px;min-height:88px;font-size:32px;background:transparent;border:3px solid var(--sarathi-ink);border-radius:14px;cursor:pointer;font-weight:700";
  back.textContent = "←";
  back.addEventListener("click", () => exitOverlay());
  const title = document.createElement("div");
  const heading = document.createElement("h1");
  heading.textContent = "Aapka ghar";
  const secondary = document.createElement("span");
  secondary.className = "secondary";
  secondary.textContent = "Your home screen";
  title.appendChild(heading);
  title.appendChild(secondary);
  header.appendChild(back);
  header.appendChild(title);
  shell.appendChild(header);

  const grid = document.createElement("div");
  grid.className = "sarathi-home-grid";
  grid.setAttribute("role", "grid");
  grid.setAttribute("aria-label", "Home tiles");
  shell.appendChild(grid);

  const renderTiles = (): void => {
    grid.innerHTML = "";
    const accepted = listAccepted();
    const tiles: readonly HomeTileSpec[] = [
      ...BASE_TILES,
      ...accepted.slice(0, 5).map((pin) => ({
        id: `pin-${pin.id}`,
        i18nKey: "",
        emoji: pin.icon || "★",
        variant: "default" as const,
        action: () => {
          window.location.href = pin.actionUrl;
        },
      })),
    ];
    for (const tile of tiles) {
      const el = document.createElement("button");
      el.type = "button";
      el.className = "sarathi-home-tile";
      el.dataset.variant = tile.variant;
      el.setAttribute("role", "gridcell");
      const glyph = document.createElement("span");
      glyph.className = "glyph";
      glyph.setAttribute("aria-hidden", "true");
      glyph.textContent = tile.emoji;
      el.appendChild(glyph);
      const label = document.createElement("span");
      if (tile.i18nKey) {
        const bi = makeBilingual(i18n, tile.i18nKey, lang);
        label.textContent = bi.primary;
        el.setAttribute("aria-label", `${bi.primary} (${bi.secondary})`);
        const sub = document.createElement("span");
        sub.style.cssText = "display:block;font-size:14px;font-weight:500;opacity:.86;margin-top:2px";
        sub.textContent = bi.secondary;
        label.appendChild(sub);
      } else if (tile.id.startsWith("pin-")) {
        const matched = listAccepted().find((p) => `pin-${p.id}` === tile.id);
        label.textContent = matched?.label ?? "Shortcut";
      }
      el.appendChild(label);
      el.addEventListener("click", () => tile.action());
      grid.appendChild(el);
    }
  };

  renderTiles();
  const unsubscribe = subscribeToPinChanges(renderTiles);

  // Quick links row
  const linksRow = document.createElement("div");
  linksRow.className = "sarathi-row";
  linksRow.style.marginTop = "32px";
  const linkSpecs: ReadonlyArray<{ label: string; sub: string; to: string }> = [
    { label: "परिवार दर्पण", sub: "Family-Mirror", to: "mirror" },
    { label: "अनुरोध", sub: "Pinned shortcuts", to: "pinned" },
    { label: "मेरी सेटिंग्स", sub: "My settings", to: "settings" },
    { label: "सेटअप", sub: "Onboarding", to: "onboard" },
  ];
  for (const ls of linkSpecs) {
    const a = document.createElement("button");
    a.type = "button";
    a.style.cssText = "min-height:88px;padding:18px;background:#fff;border:2px solid var(--sarathi-rule);border-radius:14px;font-size:20px;font-weight:600;cursor:pointer;text-align:left;color:var(--sarathi-ink);font-family:inherit";
    const lbl = document.createElement("div");
    lbl.textContent = ls.label;
    const sub = document.createElement("div");
    sub.style.cssText = "font-size:15px;color:var(--sarathi-ash);font-weight:500;margin-top:4px";
    sub.textContent = ls.sub;
    a.appendChild(lbl);
    a.appendChild(sub);
    a.addEventListener("click", () => navigate(ls.to));
    linksRow.appendChild(a);
  }
  shell.appendChild(linksRow);

  host.appendChild(shell);
  screenReaderAnnounce("Sarathi home screen", { polite: true, clearAfterMs: 4000 });

  return () => {
    unsubscribe();
    shell.remove();
  };
};
